"use client";

import { List, X } from "@phosphor-icons/react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import ButtonV2 from "./ButtonV2";

const NAVIGATION_ITEMS = [
  {
    active: false,
    name: "About",
    url: "/#about",
  },
  {
    disabled: process.env.NEXT_PUBLIC_TOKEN_MARKET_ACTIVE !== "true",
    active: false,
    name: "Tokens",
    url: "/tokens?symbol=trocko",
  },
];

function isAbsoluteURL(url: string) {
  try {
    new URL(url);
    return true;
  } catch (_: unknown) {
    return false;
  }
}

interface MobileNavigationProps {
  onClickListItem: () => void;
  isLoggedIn: boolean;
}

function MobileNavigation({ onClickListItem, isLoggedIn }: MobileNavigationProps) {
  return (
    <nav>
      <ul>
        {!isLoggedIn && (
          <>
            <li className="p-4">
              <Link
                onClick={onClickListItem}
                href="/sign-up"
                className="block w-full bg-[#00D9FF] hover:bg-[#00B8DD] text-black font-semibold text-center py-3 rounded-lg transition-colors"
              >
                Sign Up
              </Link>
            </li>
            <li className="p-4">
              <Link
                onClick={onClickListItem}
                href="/sign-in"
                className="block w-full border border-white hover:bg-white/10 text-white font-semibold text-center py-3 rounded-lg transition-colors"
              >
                Sign In
              </Link>
            </li>
          </>
        )}
        {isLoggedIn && (
          <li className="p-4">
            <Link
              onClick={onClickListItem}
              href="/admin"
              className="block w-full bg-[#00D9FF] hover:bg-[#00B8DD] text-black font-semibold text-center py-3 rounded-lg transition-colors"
            >
              Admin
            </Link>
          </li>
        )}
        {NAVIGATION_ITEMS.filter((element) => element.disabled !== true).map((element) => (
          <li key={element.url} className="text-white font-inter hover:text-opacity-75 p-4">
            <Link
              onClick={onClickListItem}
              href={element.url}
              rel="noreferrer noopener"
              target={isAbsoluteURL(element.url) ? "_blank" : undefined}
              className={twMerge(element.active && "text-white")}
            >
              {element.name}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

interface DesktopNavigationProps {
  onClickBurgerIcon: () => void;
  isLoggedIn: boolean;
  trialDaysRemaining: number | null;
}

function DesktopNavigation({ onClickBurgerIcon, isLoggedIn, trialDaysRemaining }: DesktopNavigationProps) {
  return (
    <nav className="lg:mx-16 xl:mx-24 flex flex-row items-center justify-between">
      <Link href="/">
        <Image src="/v2/kolz.svg" alt="navigation-logo" height={47} width={151} className="h-10 w-36 sm:h-14 sm:w-48" />
      </Link>

      <div className="flex items-center md:border md:border-white md:rounded-full px-3 py-0 md:backdrop-blur-md md:bg-white/10">
        <ul className="hidden md:flex items-center font-inter">
          {NAVIGATION_ITEMS.filter((element) => element.disabled !== true).map((element) => (
            <li key={element.url} className="text-white font-normal hover:text-opacity-75 p-4">
              <Link
                href={element.url}
                rel="noreferrer noopener"
                target={isAbsoluteURL(element.url) ? "_blank" : undefined}
                className={twMerge(element.active && "text-[#8D8D8D]")}
              >
                {element.name}
              </Link>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2">
          {!isLoggedIn && (
            <>
              <ButtonV2 href="/sign-in" color="secondary" size="md">
                Sign In
              </ButtonV2>
              <ButtonV2 href="/sign-up" color="primary" size="md">
                Sign Up
              </ButtonV2>
            </>
          )}
          {isLoggedIn && (
            <ButtonV2 href="/admin" color="primary" size="md">
              Admin
            </ButtonV2>
          )}
        </div>

        <button type="button" className="text-white md:hidden ml-2" onClick={() => onClickBurgerIcon()}>
          <span className="sr-only">Open sidebar</span>
          <List className="h-8 w-8 text-white" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}

interface HeaderClientProps {
  isLoggedIn: boolean;
  trialDaysRemaining: number | null;
}

export default function HeaderClient({ isLoggedIn, trialDaysRemaining }: HeaderClientProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("sm:max-md:overflow-hidden", sidebarOpen);
  }, [sidebarOpen]);

  return (
    <header className="bg-transparent p-4 z-50 relative">
      <div className="block lg:hidden">
        <div
          className={`overflow-auto bg-black fixed top-0 right-0 w-full h-full shadow-lg z-40 transform transition-transform duration-300 ${
            sidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="m-4 flex justify-between">
            <Link href="/">
              <Image
                src="/v2/kolz.svg"
                alt="navigation-logo"
                height={47}
                width={151}
                className="h-10 w-36 sm:h-14 sm:w-48"
              />
            </Link>

            <button type="button" onClick={() => setSidebarOpen(false)} className="text-white p-2">
              <X className="h-6 w-6" />
            </button>
          </div>
          <MobileNavigation onClickListItem={() => setSidebarOpen(false)} isLoggedIn={isLoggedIn} />
        </div>
      </div>
      <DesktopNavigation
        onClickBurgerIcon={() => setSidebarOpen(true)}
        isLoggedIn={isLoggedIn}
        trialDaysRemaining={trialDaysRemaining}
      />
    </header>
  );
}
